<template>
    <div>
        <div class="gps-panel" role="region" aria-label="Fonction GPS de l'appareil"
            :class="panelExpanded ? 'is-expanded' : 'is-compact'">
            <template v-if="panelExpanded">
                <div class="gps-panel-header">
                    <div class="gps-panel-title-wrap">
                        <span class="gps-panel-title">Votre position</span>
                    </div>
                    <span class="gps-status-badge" :class="gpsTrackingEnabled ? 'is-active' : 'is-idle'">
                        {{ gpsTrackingEnabled ? 'Suivi actif' : 'Suivi inactif' }}
                    </span>
                </div>

                <div class="gps-panel-details">
                    <span><strong>Coordonnées:</strong> {{ coordinatesLabel }}</span>
                    <span><strong>Précision:</strong> {{ accuracyLabel }}</span>
                </div>

                <div class="gps-panel-actions">
                    <button type="button" class="btn btn-sm"
                        :class="gpsTrackingEnabled ? 'btn-outline-danger' : 'btn-primary'" @click="toggleGpsTracking"
                        :title="gpsTrackingEnabled ? 'Désactiver le suivi GPS' : 'Activer le suivi GPS'">
                        {{ gpsTrackingEnabled ? 'Désactiver GPS' : 'Activer GPS' }}
                    </button>
                    <button type="button" class="btn btn-sm btn-light" @click="focusGpsPosition" :disabled="!gpsPosition"
                        title="Recentrer la carte sur la position GPS actuelle">
                        Recentrer
                    </button>
                </div>

                <div v-if="gpsFeedbackMessage" class="gps-panel-feedback" role="status" aria-live="polite">
                    {{ gpsFeedbackMessage }}
                </div>
            </template>

            <template v-else>
                <div class="gps-compact-row">
                    <button type="button" class="btn btn-sm btn-primary" @click="toggleGpsTracking"
                        title="Activer le GPS pour afficher votre position sur la carte">
                        Afficher ma position
                    </button>
                </div>
            </template>
        </div>

        <div v-if="!panelExpanded && gpsFeedbackMessage" class="gps-feedback-toast" role="status" aria-live="polite">
            {{ gpsFeedbackMessage }}
        </div>
    </div>
</template>

<script>
import { Geolocation } from "@capacitor/geolocation";

export default {
    name: "GpsTrackingControl",
    props: {
        map: { type: Object, default: null },
        mapGetter: { type: Function, default: null },
    },
    emits: ["position-change"],
    data() {
        return {
            gpsTrackingEnabled: false,
            gpsWatchId: null,
            gpsPosition: null,
            gpsAccuracy: null,
            gpsFeedbackMessage: "",
            gpsFeedbackTimer: null,
        };
    },
    computed: {
        coordinatesLabel() {
            if (!this.gpsPosition) {
                return "En attente";
            }
            const [lat, lng] = this.gpsPosition;
            return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        },
        accuracyLabel() {
            if (!Number.isFinite(this.gpsAccuracy)) {
                return "Indisponible";
            }
            return `${Math.round(this.gpsAccuracy)} m`;
        },
        panelExpanded() {
            return this.gpsTrackingEnabled || Boolean(this.gpsFeedbackMessage);
        },
    },
    beforeUnmount() {
        this.stopGpsTracking();
        this.clearGpsFeedback();
    },
    methods: {
        resolveMapInstance() {
            if (typeof this.mapGetter === "function") {
                const resolvedMap = this.mapGetter();
                if (resolvedMap) return resolvedMap;
            }
            return this.map;
        },
        emitGpsState() {
            this.$emit("position-change", {
                position: this.gpsPosition,
                accuracy: this.gpsAccuracy,
                trackingEnabled: this.gpsTrackingEnabled,
            });
        },
        setGpsFeedback(message, timeout = 4500) {
            this.gpsFeedbackMessage = message;

            if (this.gpsFeedbackTimer) {
                clearTimeout(this.gpsFeedbackTimer);
                this.gpsFeedbackTimer = null;
            }

            if (timeout > 0) {
                this.gpsFeedbackTimer = setTimeout(() => {
                    this.gpsFeedbackMessage = "";
                    this.gpsFeedbackTimer = null;
                }, timeout);
            }
        },
        clearGpsFeedback() {
            if (this.gpsFeedbackTimer) {
                clearTimeout(this.gpsFeedbackTimer);
                this.gpsFeedbackTimer = null;
            }
            this.gpsFeedbackMessage = "";
        },
        centerOnGpsPosition(position) {
            if (!Array.isArray(position) || position.length !== 2) return;
            const map = this.resolveMapInstance();
            if (!map) return;

            const nextZoom = Math.max(map.getZoom(), 16);
            map.setView(position, nextZoom, { animate: true });
        },
        focusGpsPosition() {
            if (!this.gpsPosition) {
                this.setGpsFeedback("Aucune position GPS disponible pour le moment.");
                return;
            }
            this.centerOnGpsPosition(this.gpsPosition);
        },
        handleGpsPosition(position) {
            if (!position || !position.coords) return;

            const lat = Number(position.coords.latitude);
            const lng = Number(position.coords.longitude);
            const accuracy = Number(position.coords.accuracy);

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

            this.gpsPosition = [lat, lng];
            this.gpsAccuracy = Number.isFinite(accuracy) ? accuracy : null;
            this.emitGpsState();
            this.centerOnGpsPosition(this.gpsPosition);
        },
        async ensureNativeLocationPermission() {
            let permissions = await Geolocation.checkPermissions();
            if (permissions.location === "granted" || permissions.coarseLocation === "granted") {
                return true;
            }

            permissions = await Geolocation.requestPermissions();
            return permissions.location === "granted" || permissions.coarseLocation === "granted";
        },
        async startGpsTracking() {
            if (this.gpsTrackingEnabled) return;

            this.clearGpsFeedback();

            try {
                const hasPermission = await this.ensureNativeLocationPermission();
                if (!hasPermission) {
                    this.setGpsFeedback("Accès à la localisation refusé. Vérifiez les autorisations de l'application dans les réglages de votre appareil.");
                    return;
                }

                this.gpsWatchId = await Geolocation.watchPosition(
                    {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 0,
                    },
                    (position, err) => {
                        if (err || !position) {
                            if (err) {
                                this.setGpsFeedback("Impossible de récupérer votre position GPS. Vérifiez que la localisation est activée sur votre appareil.");
                            }
                            return;
                        }
                        this.handleGpsPosition(position);
                    }
                );

                this.gpsTrackingEnabled = true;
                this.emitGpsState();
                this.setGpsFeedback("Suivi GPS activé.", 2200);

                const initial = await Geolocation.getCurrentPosition({
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0,
                });
                this.handleGpsPosition(initial);
            } catch (error) {
                console.error("Impossible d'activer le suivi GPS", error);
                this.gpsTrackingEnabled = false;
                this.emitGpsState();
                this.setGpsFeedback("Impossible d'activer le GPS : vérifiez les permissions de l'application sur votre appareil.");
            }
        },
        async stopGpsTracking() {
            if (!this.gpsWatchId) {
                this.gpsTrackingEnabled = false;
                this.gpsPosition = null;
                this.gpsAccuracy = null;
                this.emitGpsState();
                return;
            }

            try {
                await Geolocation.clearWatch({ id: this.gpsWatchId });
            } catch (error) {
                console.error("Impossible de desactiver le suivi GPS", error);
                this.setGpsFeedback("Impossible de désactiver le suivi GPS pour le moment.");
            } finally {
                this.gpsWatchId = null;
                this.gpsTrackingEnabled = false;
                this.gpsPosition = null;
                this.gpsAccuracy = null;
                this.emitGpsState();
                this.setGpsFeedback("Suivi GPS désactivé.", 2200);
            }
        },
        async toggleGpsTracking() {
            if (this.gpsTrackingEnabled) {
                await this.stopGpsTracking();
                return;
            }

            await this.startGpsTracking();
        },
    },
};
</script>

<style scoped>
.gps-panel {
    position: absolute;
    left: 1rem;
    bottom: 6.8rem;
    z-index: 2000;
    width: min(360px, 92vw);
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid rgba(13, 110, 253, 0.22);
    border-radius: 12px;
    box-shadow: 0 10px 28px rgba(7, 24, 60, 0.16);
    padding: 0.75rem 0.8rem;
    backdrop-filter: blur(4px);
}

.gps-panel.is-compact {
    width: auto;
    min-width: 0;
    padding: 0.42rem 0.5rem;
    background: rgba(255, 255, 255, 0.9);
}

.gps-compact-row {
    display: flex;
    align-items: center;
    justify-content: center;
}

.gps-panel.is-compact .btn {
    white-space: nowrap;
    font-weight: 700;
    border-radius: 9px;
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.2);
}

.gps-panel-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.7rem;
}

.gps-panel-title-wrap {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.gps-panel-title {
    font-size: 0.95rem;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.1;
}

.gps-status-badge {
    font-size: 0.72rem;
    font-weight: 700;
    border-radius: 999px;
    padding: 0.2rem 0.55rem;
    border: 1px solid transparent;
    white-space: nowrap;
}

.gps-status-badge.is-active {
    color: #0b5130;
    background: #d1f4e4;
    border-color: #83dfb5;
}

.gps-status-badge.is-idle {
    color: #5b6674;
    background: #eef1f5;
    border-color: #d8dee7;
}

.gps-panel-details {
    margin-top: 0.55rem;
    display: grid;
    gap: 0.2rem;
    font-size: 0.75rem;
    color: #253447;
}

.gps-panel-actions {
    margin-top: 0.68rem;
    display: flex;
    gap: 0.48rem;
}

.gps-panel-actions .btn {
    flex: 1;
    font-weight: 700;
}

.gps-panel-feedback {
    margin-top: 0.6rem;
    background: rgba(220, 53, 69, 0.12);
    color: #842029;
    border: 1px solid rgba(220, 53, 69, 0.24);
    border-radius: 8px;
    padding: 0.48rem 0.58rem;
    font-size: 0.78rem;
    font-weight: 600;
}

.gps-feedback-toast {
    position: absolute;
    left: 1rem;
    bottom: 1rem;
    z-index: 2000;
    width: min(320px, 80vw);
    background: rgba(220, 53, 69, 0.92);
    color: #fff;
    border-radius: 8px;
    padding: 0.45rem 0.58rem;
    font-size: 0.74rem;
    font-weight: 700;
    box-shadow: 0 8px 18px rgba(20, 18, 18, 0.24);
}

@media (min-width: 769px) {
    .gps-panel {
        width: 350px;
    }

    .gps-panel.is-compact {
        width: auto;
    }
}
</style>